import React, { useState, useEffect }  from 'react';
import { useParams, useHistory, Link as RouterLink } from "react-router-dom"
import { makeStyles } from '@material-ui/core/styles';
import { DateTime } from 'luxon';
import { DatePicker } from "@material-ui/pickers";

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import CardHeader from '@material-ui/core/CardHeader';
import Avatar from '@material-ui/core/Avatar';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import MenuItem from '@material-ui/core/MenuItem'

import ListSubheader from '@material-ui/core/ListSubheader';
import ListItemText from '@material-ui/core/ListItemText';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import ClearIcon from '@material-ui/icons/Clear';
import DeleteIcon from '@material-ui/icons/Delete';
import InfoIcon from '@material-ui/icons/Info';

import SaveButton from 'app/components/buttons/SaveButton';
import Loading from 'app/components/loaders/Loading';
import SearchUsersAutoComplete from 'app/components/user/UserSearchAutocomplete';

import { paper } from 'app/styles/core';
import { container } from 'app/styles/new-layout';

import useApi from 'app/context/useApi';
import useQuery from 'app/hooks/useQuery';

const useStyles = makeStyles(theme => ({
    container: {
        ...container(theme),
        padding: theme.spacing(3)
    },
    paper: {
        ...paper(theme),
        position: 'relative'
    },
    invoice: {
        padding: theme.spacing(3),
        borderRadius: theme.shape.borderRadius,
        border: '2px solid',
        borderColor: theme.palette.secondary.main
    },
    section: {
        marginBottom: theme.spacing(3)
    },
    avatar: {
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    },
    marginRight: {
        marginRight: theme.spacing(3)
    },
    marginLeft: {
        marginLeft: theme.spacing(3)
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    listItem: {
        display: 'block'
    }
}));

const renderDateInput = props => (
    <TextField
        variant="outlined"
        fullWidth
        margin="normal"
        onClick={props.onClick}
        id={props.id}
        value={props.value}
        disabled={props.disabled}
        label={props.label}
        {...props.inputProps}
    />
);

const InvoiceItem = props => {

    const classes = useStyles();

    const {
        item,
        index,
        levels,
        events,
        pastItems,
        handleChange,
        removeItem,
        isError,
        getErrorMessage
    } = props;

    const namefield = () => {
        switch (item.type) {
            case 'dues': return (
                <TextField
                    select
                    value={item.level_id}
                    onChange={(e) => handleChange(e, 'level_id', index)}
                    helperText={getErrorMessage('level_id', index)}
                    error={isError('level_id', index)}
                    variant="outlined"
                    fullWidth
                    color="primary"
                    size="small"
                    >
                    <MenuItem value={0}>
                        Please select a Level...
                    </MenuItem>
                    {levels.map(level => (
                        <MenuItem key={level.id} value={level.id}>
                            {level.title}
                        </MenuItem>
                    ))}
                </TextField>
            )
            case 'past-item': return (
                <TextField
                    select
                    value={item.past_item_id}
                    onChange={(e) => handleChange(e, 'past_item_id', index)}
                    helperText={getErrorMessage('past_item_id', index)}
                    error={isError('past_item_id', index)}
                    variant="outlined"
                    fullWidth
                    color="primary"
                    size="small"
                    >
                    <MenuItem value={0}>
                        Please select a previously used item...
                    </MenuItem>
                    {pastItems.map(item => (
                        <MenuItem key={item.id} value={item.id}>
                            {item.name}
                        </MenuItem>
                    ))}
                </TextField>
            )
            case 'ticket': return (
                <TextField
                    select
                    variant="outlined"
                    fullWidth
                    color="primary"
                    size="small"
                    value={item.ticket_id}
                    onChange={(e) => handleChange(e, 'ticket_id', index)}
                    helperText={getErrorMessage('ticket_id', index)}
                    error={isError('ticket_id', index)}
                    >
                    <MenuItem value={0}>
                        Please select a Ticket...
                    </MenuItem>
                    {events.map(ev => (
                        <>
                            <ListSubheader>{ev.name}</ListSubheader>
                            {ev.tickets.map(t => (
                                <MenuItem key={t.id} value={t.id}>
                                    <ListItemText
                                        primary={t.title}
                                        secondary={(
                                            <span className={classes.marginTop}>
                                                <Typography component="span" variant="subtitle2" color="textPrimary">Current Price:&nbsp;&nbsp;</Typography>
                                                <Typography component="span" variant="subtitle2" color="primary">${t.current_price_f}</Typography><br/>
                                                <Typography variant="subtitle2" color="textPrimary">
                                                    Amount Left: <strong>{(t.amount > 0) ? t.amount_left : 'Unlimited'}</strong>
                                                </Typography>
                                            </span>
                                        )}
                                    />
                                </MenuItem>
                            ))}
                        </>
                    ))}
                </TextField>
            )
            default: return (
                <TextField
                    label="Item Name"
                    value={item.name}
                    onChange={(e) => handleChange(e, 'name', index)}
                    fullWidth
                    error={isError('name', index)}
                    helperText={getErrorMessage('name', index)}
                    variant="outlined"
                    size="small"
                />
            )
        }
    }

    return (
        <TableRow>
            <TableCell component="th" scope="row" colSpan={(item.type === 'item') ? 1 : 2}>
                {namefield()}
            </TableCell>
            {item.type === 'item' &&
                <TableCell align="right">
                    <TextField
                        label="Description"
                        value={item.description}
                        onChange={e => handleChange(e, 'description', index)}
                        fullWidth
                        error={isError('description', index)}
                        helperText={getErrorMessage('description', index)}
                        variant="outlined"
                        size="small"
                    />
                </TableCell>
            }
            <TableCell align="right">
                <TextField
                    label="Quantity"
                    type="number"
                    min="1"
                    step="1"
                    value={item.quantity}
                    onChange={e => handleChange(e, 'quantity', index)}
                    fullWidth
                    error={isError('quantity', index)}
                    helperText={getErrorMessage('quantity', index)}
                    variant="outlined"
                    size="small"
                />
            </TableCell>
            <TableCell align="right">
                <TextField
                    label="Price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.amount}
                    onChange={e => handleChange(e, 'amount', index)}
                    fullWidth
                    error={isError('amount', index)}
                    helperText={getErrorMessage('amount', index)}
                    variant="outlined"
                    size="small"
                />
            </TableCell>
            <TableCell align="right">
                ${(item.quantity * item.amount).toFixed(2)}
            </TableCell>
            <TableCell>
                <IconButton onClick={() => removeItem(index)}>
                    <DeleteIcon/>
                </IconButton>
            </TableCell>
        </TableRow>
    )
}

const newInvoiceItem = {
    id: 0,
    level_id: 0,
    ticket_id: 0,
    past_item_id: 0,
    name: "",
    description: "",
    quantity: 1,
    amount: 0.00,
    type: 'item'
}

export default function EditInvoice(props) {

    let { id } = useParams();
    let query = useQuery();
    let history = useHistory();
    const api = useApi();

    const user_id = query.get('user_id');

    const [ invoice, setInvoice ] = useState({
        invoice_number: 0,
        user_id: 0,
        group_id: 0,
        description: "",
        reporting_category: "",
        notes: "",
        include_convenience_fees: false,
        send_invoice_email: true,
        send_late_email: true,
        invoice_by: DateTime.local().startOf('day'),
        due_by: DateTime.local().startOf('day').plus({days: 15}),
        include_children: false
    })

    const [ items, setItems ] = useState([]);

    const [ user, setUser ] = useState({ id: 0 })
    const [ levels, setLevels ] = useState([]);
    const [ groups, setGroups ] = useState([]);
    const [ events, setEvents ] = useState([]);
    const [ pastItems, setPastItems ] = useState([]);

    const [ invoiceGroup, setInvoiceGroup ] = useState(false)

    const [ loading, setLoading ] = useState(false);
    const [ errors, setErrors ] = useState({});

    const classes = useStyles();

    // GETTERS ------------------------------------------------

    const getInvoice = () => {
        setLoading(true);

        api.get('/admin/accounting/invoice/' + id)
            .then((res) => {
                setInvoice({
                    invoice_number: res.invoice.invoice_number,
                    user_id: res.invoice.user_id,
                    description: res.invoice.description,
                    reporting_category: res.invoice.reporting_category || "",
                    notes: res.invoice.notes || "",
                    include_convenience_fees: res.invoice.include_convenience_fees,
                    send_invoice_email: res.invoice.send_invoice_email,
                    send_late_email: res.invoice.send_late_email,
                    invoice_by: DateTime.fromISO(res.invoice.invoice_by),
                    due_by: DateTime.fromISO(res.invoice.due_by),
                })
                setUser(res.invoice.user);
                setItems(res.invoice.invoice_items);
            }).finally(() => setLoading(false));
    }

    // gets and sets the next available invoice number
    const getInvoiceNumber = () => {
        setLoading(true);

        api.get('/admin/accounting/invoice/number')
            .then((res) => {
                if (user_id) getUser(res.number);
                else {
                    setInvoice({...invoice, invoice_number: res.number})
                    setLoading(false)
                }
            }).catch(() => setLoading(false));
    }

    const getUser = invoice_number => {
        api.get(`/admin/user/${user_id}`)
            .then(res => {
                setUser(res.user);
                setInvoice({...invoice, invoice_number: invoice_number, user_id: res.user.id})
            })
            .finally(() => setLoading(false));
    }

    const getEvents= () => api.get(`/admin/event/ticket`).then(res => setEvents(res.events));

    const getLevels = () => api.get('/admin/group/level').then(res => setLevels(res.groups));

    const getGroups = () => api.get('/admin/group').then(res => setGroups(res.groups));

    const getPastItems = () => api.get(`/admin/accounting/items`).then(res => setPastItems(res.items));

    useEffect(() => {
        (id > 0) ? getInvoice() : getInvoiceNumber();

        getEvents();
        getLevels();
        getGroups();
        getPastItems();

    }, []);

    // SETTERS ------------------------------------------------

    const handleChange = name => event => {
        const target = event.target;
        const value = (target.type === 'checkbox' ? target.checked : target.value);

        if (isError(name)) delete errors[name];

        setInvoice({ ...invoice, [name]: value });
    };

    const handleInvoiceBy = value => {
        setInvoice({...invoice, invoice_by: value })
    }

    const handleDueBy = value => {
        setInvoice({...invoice, due_by: value })
    }

    const handleItemChange = (event, name, i) => {

        const target = event.target;
        const value = (target.type === 'checkbox' ? target.checked : target.value);

        if (isInvoiceItemError(name)) delete errors.invoice_items[i][name];

        const newItems = [...items];
        const newItem = {...items[i]};

        switch (name) {
            case 'level_id':
                const level = levels.find(l => l.id === value);

                newItem['name'] = 'Membership Fees ' + level.date;
                newItem['description'] = 'Invoice Item for Membership Fees';
                newItem['amount'] = level.fee;

                break;
            case 'ticket_id':
                const ticket = events.find(e => e.find(t => t.id === value));
            
                newItem['name'] = ticket.title;
                newItem['description'] = ticket.description;
                newItem['amount'] = ticket.current_price;

                break;
            case 'past_item_id':
                const pastItem = pastItems.find(i => i.id === value);

                newItem['name'] = pastItem.name;
                newItem['description'] = pastItem.description;
                newItem['amount'] = pastItem.amount;

                break;
        }

        newItem[name] = value;

        newItems[i] = newItem;

        setItems(newItems);
    };

    const handleUserId = user => {
        if (isError('user_id')) delete errors.user_id;

        setInvoice({...invoice, user_id: user.id })
        setUser(user);
    }

    const createInvoiceItem = type => {
        const newItems = [...items];
        const newItem = {...newInvoiceItem, type: type};

        newItems.push(newItem);
        setItems(newItems);
    }

    const removeInvoiceItem = i => {
        const newItems = [...items];
        newItems.splice(i, 1);
        setItems(newItems)
    }

    const toggleInvoiceGroup = () => {
        setInvoiceGroup(!invoiceGroup);
    }

    // POSTERS ------------------------------------------------

    const postInvoice = () => {
        setLoading(true);

        const data = {...invoice, invoice_items: items, invoice_group: invoiceGroup }

        const url = invoiceGroup ? `/admin/accounting/invoice/batch` : `/admin/accounting/invoice/${id}`;

        api.post(url, data)
            .then((res) => {
                history.push('/admin/accounting/invoices/view/' + res.id);
            })
            .catch(error => {
                setLoading(false)

                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            });
    }

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    const isInvoiceItemError = (field, i) => {
        if (errors[`invoice_items.${i}.${field}`]) return true;
        return false;
    }

    const getInvoiceItemErrorMessage = (field, i) => {
        if (isInvoiceItemError(field)) return errors[`invoice_items.${i}.${field}`].split('.')[2];
        return "";
    }

    const getReportingCategoryMessage = () => {
        if (isError('reporting_category')) return errors.reporting_category;
        return "Use this to add an internal reporting category. This will be sent to stripe as well for generating reports."
    }

    // CONSTANTS ------------------------------------------------

    const initials = user => user.first.charAt(0) + user.last.charAt(0);

    const subTotal = items.reduce((total, item) => {
        return total + (item.quantity * item.amount)
    }, 0).toFixed(2);

    const staticFee = 0.30
    const dynamicFee = 3.9;

    const userPays = ((parseFloat(subTotal) + staticFee) / (1 - (dynamicFee / 100))).toFixed(2);

    const convenienceFee = (((invoice.include_convenience_fees ? userPays : subTotal) * (dynamicFee / 100)) + staticFee).toFixed(2);

    const netTotal = ((invoice.include_convenience_fees ? userPays : subTotal) - convenienceFee).toFixed(2);

    return (
        <div className={classes.container}>
            <Paper className={classes.paper}>
                <form noValidate autoComplete="off">
                    <Loading isLoading={loading}/>
                    <Grid
                        container
                        alignItems="center"
                        justify="space-between"
                        spacing={3}
                        className={classes.marginBottom}
                        >
                        <Grid item>
                            <Typography variant="h4" component="h2">
                                {id == 0 ? "New Invoice" : "Edit Invoice"}
                            </Typography>
                        </Grid>
                        <Grid item>
                            {user_id &&
                                <Button
                                    to={`/admin/users/${user_id}/payment`}
                                    component={RouterLink}
                                    variant="outlined"
                                    color="primary"
                                    className={classes.marginRight}
                                    >
                                    Back to User
                                </Button>
                            }
                            {id > 0 &&
                                <Button
                                    to={"/admin/accounting/invoices/view/" + id}
                                    component={RouterLink}
                                    variant="outlined"
                                    color="primary"
                                    className={classes.marginRight}
                                    >
                                    View Invoice
                                </Button>
                            }
                            <Button
                                to="/admin/accounting/invoices"
                                component={RouterLink}
                                variant="outlined"
                                color="primary"
                                >
                                All Invoices
                            </Button>
                        </Grid>
                    </Grid>
                    <div className={classes.invoice}>
                        <Grid container spacing={3} className={classes.section}>
                            <Grid item xs={6}>
                                <Typography variant="h6" color="textSecondary" gutterBottom>
                                    Invoice Number:
                                </Typography>
                                <TextField
                                    placeholder="Number used to identify the invoice"
                                    value={invoice.invoice_number}
                                    onChange={handleChange('invoice_number')}
                                    fullWidth
                                    error={isError('invoice_number')}
                                    helperText={getErrorMessage('invoice_number')}
                                    variant="outlined"
                                    className={classes.marginBottom}
                                    disabled={invoiceGroup}
                                />
                                <Grid container justifyContent='space-between' alignItems='center' spacing={3}>
                                    <Grid item>
                                        <Typography variant="h6" color="textSecondary" gutterBottom>
                                            Invoice For:
                                        </Typography>
                                    </Grid>
                                    {(id == 0) &&
                                        <Grid item>
                                            <Typography component="div" variant='subtitle2' color="textSecondary">
                                                <Grid component="label" container alignItems="center" spacing={1}>
                                                    <Grid item>User</Grid>
                                                    <Grid item>
                                                        <Switch checked={invoiceGroup} onChange={toggleInvoiceGroup} color="primary"/>
                                                    </Grid>
                                                    <Grid item>Group</Grid>
                                                </Grid>
                                            </Typography>
                                        </Grid>
                                    }
                                </Grid>
                                {invoiceGroup ? (
                                    <>
                                        <TextField
                                            id="group-select"
                                            select
                                            value={invoice.group_id}
                                            onChange={handleChange('group_id')}
                                            helperText={getErrorMessage('group_id')}
                                            error={isError('group_id')}
                                            variant="outlined"
                                            fullWidth
                                            color="primary"
                                            margin="normal"
                                            >
                                            <MenuItem key={0} value={0}>
                                                All Users
                                            </MenuItem>
                                            {groups.map(group => (
                                                <MenuItem key={group.id} value={group.id}>
                                                    {group.title}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={invoice.include_children}
                                                    onChange={handleChange('include_children')}
                                                    name="include_children"
                                                    color="primary"
                                                />
                                            }
                                            label="Send to Child Groups"
                                        />
                                    </>

                                ) : (
                                    <>
                                        {(user.id > 0) ? (
                                            <CardHeader
                                                avatar={
                                                    <Avatar
                                                        aria-label={user.full_name}
                                                        alt={user.full_name}
                                                        className={classes.avatar}
                                                        src={user.avatar ? user.avatar.full_url : null}
                                                        >
                                                        {initials(user)}
                                                    </Avatar>
                                                }
                                                title={user.full_name}
                                                subheader={user.membership_title}
                                                action={
                                                    <IconButton aria-label="Remove User" onClick={() => handleUserId({id:0})}>
                                                        <ClearIcon />
                                                    </IconButton>
                                                }
                                            />
                                        ) : (
                                            <SearchUsersAutoComplete
                                                closeOnSelect
                                                setOnSelect
                                                onSelect={handleUserId}
                                                label="Find User to Invoice"
                                            />
                                        )}
                                    </>
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="h6" color="textSecondary" gutterBottom>
                                    Details:
                                </Typography>
                                <TextField
                                    label="Invoice Description"
                                    multiline
                                    rows="3"
                                    rowsMax="3"
                                    value={invoice.description}
                                    onChange={handleChange('description')}
                                    fullWidth
                                    error={isError('description')}
                                    helperText={getErrorMessage('description')}
                                    variant="outlined"
                                />
                                <DatePicker
                                    label="Invoice Date"
                                    inputVariant="outlined"
                                    value={invoice.invoice_by}
                                    onChange={handleInvoiceBy}
                                    TextFieldComponent={renderDateInput}
                                /><br/>
                                <DatePicker
                                    label="Due Date"
                                    inputVariant="outlined"
                                    value={invoice.due_by}
                                    onChange={handleDueBy}
                                    TextFieldComponent={renderDateInput}
                                />
                            </Grid>
                        </Grid>
                        <Typography variant="h6" color="textSecondary" gutterBottom>
                            Invoice Items:
                        </Typography>
                        <TableContainer component={Paper} className={classes.section} elevation={3}>
                            <Table className={classes.table} aria-label="Invoice Items">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Item</TableCell>
                                        <TableCell>Description</TableCell>
                                        <TableCell align="right">Quantity</TableCell>
                                        <TableCell align="right">Price</TableCell>
                                        <TableCell align="right">Amount</TableCell>
                                        <TableCell/>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.length ? items.map((item, i) => (
                                        <InvoiceItem
                                            key={i}
                                            item={item}
                                            index={i}
                                            levels={levels}
                                            events={events}
                                            pastItems={pastItems}
                                            handleChange={handleItemChange}
                                            removeItem={removeInvoiceItem}
                                            isError={isInvoiceItemError}
                                            getErrorMessage={getInvoiceItemErrorMessage}
                                        />
                                    )) : (
                                        <TableRow>
                                            <TableCell component="th" scope="row" colSpan={6}>
                                                <Typography variant="subtitle2" align="center">
                                                    Click an option below to add an Invoice Item.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    <TableRow>
                                        <TableCell component="th" scope="row" colSpan={6}>
                                            <Button
                                                color="primary"
                                                onClick={() => createInvoiceItem('item')}
                                                variant="outlined"
                                                >
                                                Add New Item
                                            </Button>
                                            <Button
                                                color="primary"
                                                onClick={() => createInvoiceItem('past-item')}
                                                variant="outlined"
                                                className={classes.marginLeft}
                                                disabled={pastItems.length === 0}
                                                >
                                                Add Past Item
                                            </Button>
                                            <Button
                                                color="primary"
                                                onClick={() => createInvoiceItem('dues')}
                                                variant="outlined"
                                                className={classes.marginLeft}
                                                >
                                                Add Membership Dues
                                            </Button>
                                            <Button
                                                color="primary"
                                                onClick={() => createInvoiceItem('ticket')}
                                                variant="outlined"
                                                className={classes.marginLeft}
                                                disabled={true} // Need to figure out how to add guest and invitation information first. Maybe only allow after the event is over? Would disrupt reporting
                                                >
                                                Add Ticket
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" scope="row" colSpan={4}>
                                            <Typography variant="subtitle2" align="right">
                                                Invoice Total:
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography color="primary" variant="subtitle2">
                                                ${subTotal}
                                            </Typography>
                                        </TableCell>
                                        <TableCell/>
                                    </TableRow>
                                    {invoice.include_convenience_fees &&
                                        <TableRow>
                                            <TableCell component="th" scope="row" colSpan={4}>
                                                <Typography variant="subtitle2" align="right" color="textSecondary">
                                                    (Only if Using our Payment Processor) User Pays:
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography color="textSecondary" variant="subtitle2">
                                                    ${subTotal > 0 ? userPays : "0.00"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell/>
                                        </TableRow>
                                    }
                                    <TableRow>
                                        <TableCell component="th" scope="row" colSpan={2}>
                                            <Button
                                                size="small"
                                                target="_blank"
                                                color="primary"
                                                href="https://www.valuepenguin.com/credit-card-surcharges-convenience-fees"
                                                startIcon={<InfoIcon />}
                                                >
                                                Know your local laws on convenience fees
                                            </Button>
                                        </TableCell>
                                        <TableCell component="th" scope="row" colSpan={2}>
                                            <Typography variant="subtitle2" align="right" color="textSecondary">
                                                (Only if Using our Payment Processor) Convenience Fee:<br/>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={invoice.include_convenience_fees}
                                                            onChange={handleChange("include_convenience_fees")}
                                                            name="include_convenience_fees"
                                                            color="primary"
                                                            size="small"
                                                        />
                                                    }
                                                    label="Pass Fee to User?"
                                                    margin="normal"
                                                />
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography color="textSecondary" variant="subtitle2">
                                                -$({subTotal > 0 ? convenienceFee : "0.00"})
                                            </Typography>
                                        </TableCell>
                                        <TableCell/>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" scope="row" colSpan={4}>
                                            <Typography variant="subtitle2" align="right" color="textSecondary">
                                                Net Total:
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography color="primary" variant="subtitle2">
                                                ${subTotal > 0 ? netTotal : "0.00"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell/>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Grid container spacing={3}>
                            <Grid item xs={6}/>
                            <Grid item xs={6}>
                                <TextField
                                    label="Reporting Category"
                                    placeholder="GL Account Number: 564010"
                                    value={invoice.reporting_category}
                                    onChange={handleChange('reporting_category')}
                                    fullWidth
                                    error={isError('reporting_category')}
                                    helperText={getReportingCategoryMessage()}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={6}/>
                            <Grid item xs={6}>
                                <TextField
                                    label="Internal Notes"
                                    placeholder="Use this to make internal notes about this invoice. The invoiced user is not capable of viewing this."
                                    multiline
                                    rows="3"
                                    rowsMax="3"
                                    value={invoice.notes}
                                    onChange={handleChange('notes')}
                                    fullWidth
                                    error={isError('notes')}
                                    helperText={getErrorMessage('notes')}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={6}/>
                            <Grid item xs={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={invoice.send_invoice_email}
                                            onChange={handleChange("send_invoice_email")}
                                            name="send_invoice_email"
                                            color="primary"
                                        />
                                    }
                                    label="Send User the Invoice by Email?"
                                    margin="normal"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={invoice.send_late_email}
                                            onChange={handleChange("send_late_email")}
                                            name="send_late_email"
                                            color="primary"
                                        />
                                    }
                                    label="Send User a Late Email if not paid by Due Date?"
                                    margin="normal"
                                />
                            </Grid>
                        </Grid>
                    </div>
                    <Typography align="right" component="div" className={classes.marginTop}>
                        <SaveButton
                            disabled={loading}
                            variant="contained"
                            color="primary"
                            onClick={postInvoice}
                            >
                            {id == 0 ? "Create Invoice" : "Update Invoice"}
                        </SaveButton>
                    </Typography>
                </form>
            </Paper>
        </div>
    )
}
